import { Field, Form, withFormik } from "formik";
import { bool, arrayOf, func, shape, string } from "prop-types";
import React, { Component } from "react";
import { connect } from "react-redux";
import { goBack, push } from "react-router-redux";
import { compose } from "redux";
import Yup from "yup";

import { flattenArray, removeDuplicateItems } from "util/array";
import * as i18n from "util/i18n";

import { selectors as ecomexSelector } from "reducers/ecomex";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import { actions as holidaysActions, selectors as holidaysSelector } from "reducers/holidays";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as transactionsSelectors } from "reducers/transactions";
import { selectors as transferSelectors } from "reducers/transfer";

import Button from "pages/_components/Button";
import Container from "pages/_components/Container";
import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import Credential from "pages/_components/fields/credentials/Credential";
import Col from "react-bootstrap/lib/Col";
import isTokenActive from "util/token";
import {
    applyEmbeddedV3ShellLayoutState,
    clearEmbeddedV3ShellLayoutState,
    isUxDashboardImprovementsEnabled,
} from "util/v3ShellLayoutSync";

/**
 * Destino del botón volver en firma: prioriza state de la navegación; si falta, usa el último pathname
 * persistido (p. ej. ticket previo a /transactions/sign cuando el push no envió state).
 */
function resolveTransactionSignGoBackPath(location, lastHrefPathname) {
    const fromState = location?.state?.goBackState;
    if (typeof fromState === "string" && fromState.startsWith("/")) {
        return fromState;
    }
    if (
        typeof lastHrefPathname === "string" &&
        lastHrefPathname.startsWith("/") &&
        lastHrefPathname !== "/transactions/sign" &&
        lastHrefPathname !== "/transactions/cancel"
    ) {
        return lastHrefPathname;
    }
    return "/v3/dashboard";
}

class TransactionTicketSignConfirmation extends Component {
    static propTypes = {
        isDesktop: bool.isRequired,
        dispatch: func.isRequired,
        credentials: arrayOf(string).isRequired,
        fetching: bool.isRequired,
        signTransactionData: shape({}).isRequired,
        summary: shape({}).isRequired,
        isSuccessSign: bool.isRequired,
        location: shape({
            state: shape({
                goBackState: string,
            }),
        }),
        lastHrefPathname: string,
    };

    static defaultProps = {
        location: null,
        lastHrefPathname: "",
    };

    componentDidMount() {
        const { dispatch } = this.props;
        dispatch(holidaysActions.listHolidays());
        this.syncEmbeddedV3ShellLayout();
    }

    componentDidUpdate(prevProps) {
        const { dispatch, signTransactionData, summary, isSuccessSign, isDesktop, location } = this.props;
        if (isSuccessSign !== prevProps.isSuccessSign && isSuccessSign === false) {
            const issetScheduler = summary?.scheduler != null && Object.keys(summary?.scheduler).length > 0;
            const isSameActivity = signTransactionData?.idActivity === summary?.idActivity;
            if (isSameActivity && summary?.transferKind && summary?.tadTrx && !issetScheduler) {
                dispatch(
                    formActions.updateScheduledTransactionRequest({
                        idTransaction: signTransactionData?.idTransaction,
                        schedulerData: {},
                    }),
                );
            }
        }

        const { lastHrefPathname } = this.props;
        if (
            prevProps.isDesktop !== isDesktop ||
            prevProps.signTransactionData !== signTransactionData ||
            prevProps.location?.state !== location?.state ||
            prevProps.lastHrefPathname !== lastHrefPathname
        ) {
            this.syncEmbeddedV3ShellLayout();
        }
    }

    componentWillUnmount() {
        const { dispatch } = this.props;
        if (isUxDashboardImprovementsEnabled()) {
            clearEmbeddedV3ShellLayoutState(dispatch);
        }
    }

    syncEmbeddedV3ShellLayout = () => {
        const { dispatch, isDesktop, signTransactionData, location, lastHrefPathname } = this.props;
        const uxDashboardImprovements = isUxDashboardImprovementsEnabled();
        const goBackPath = resolveTransactionSignGoBackPath(location, lastHrefPathname);
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell: uxDashboardImprovements,
            isDesktop,
            scopes: ["signature"],
            titleKey: signTransactionData?.title || "",
            goBackPath,
        });
    };

    handleBack = () => {
        const { dispatch, location, lastHrefPathname } = this.props;
        const path = resolveTransactionSignGoBackPath(location, lastHrefPathname);
        if (isUxDashboardImprovementsEnabled()) {
            dispatch(push(path));
            return;
        }
        dispatch(goBack());
    };

    centerContentMobile = () => {
        const { signTransactionData } = this.props;
        return (
            <div className="title-account-header-multiline-background-blue">
                <h1 style={{ margin: "0.2rem" }}>{i18n.get(signTransactionData?.title).replace(/\*/g, "")}</h1>
            </div>
        );
    };

    render() {
        const { credentials, fetching, signTransactionData, isDesktop } = this.props;
        const uxDashboardImprovements = isUxDashboardImprovementsEnabled();

        return (
            <>
                {!uxDashboardImprovements && <Notification scopeToShow="signature" />}
                <div className={isDesktop ? "admin-detail-head px-0" : ""}>
                    {(!uxDashboardImprovements || isDesktop) && (
                        <Head
                            onBack={this.handleBack}
                            headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                            centerElement={
                                isDesktop || !signTransactionData?.title ? undefined : this.centerContentMobile
                            }
                        />
                    )}
                    {isDesktop && (
                        <div className="view-title">
                            {signTransactionData?.title !== undefined ? (
                                <h1>{i18n.get(signTransactionData?.title)}</h1>
                            ) : null}
                        </div>
                    )}
                </div>
                <MainContainer>
                    <Form className="confirmation__container">
                        {signTransactionData?.title !== undefined ? (
                            <Container
                                className={`align-items-center flex-grow px-0 container--layout with-margin mb-0 
                                    ${!isDesktop && "mb-0 cmf-container-white"}`}
                                gridClassName="confirmation__form confirmation__box">
                                <div className="admin-content-center mb-3">
                                    <I18n id="sign.transaction" component="h2" />
                                </div>
                                <Col className="col-12" lg={12} md={12} sm={12}>
                                    {signTransactionData?.content}

                                    {!isTokenActive(!isDesktop) ? (
                                        <div className="admin-content-center">
                                            <I18n id="form.credential.hint" component="p" />
                                        </div>
                                    ) : (
                                        <div className="admin-content-center pb-3">
                                            <I18n
                                                id="confirmation.withoutotp.text"
                                                component="p"
                                                componentProps={{ className: "confirmation__text-without-otp" }}
                                            />
                                        </div>
                                    )}
                                    {credentials.map((credential) => (
                                        <div key={credential}>
                                            <Field
                                                idForm="form.credential"
                                                name={credential}
                                                component={Credential}
                                                type={credential}
                                                placeholderText={i18n.get(`confirmation.otp.placeholder`)}
                                            />
                                        </div>
                                    ))}
                                    <Container
                                        className="flex-grow align-items-center w-100"
                                        gridClassName="form-content">
                                        <Col className="col-12 confirmation__content-button" lg={12} md={12} sm={12}>
                                            <div className="admin-content-center">
                                                <Button
                                                    type="submit"
                                                    label="global.confirm"
                                                    bsStyle="primary"
                                                    loading={fetching}
                                                    image="images/arrowRight.svg"
                                                    imageStyle={isDesktop ? "mr-2" : "mr-1"}
                                                />
                                            </div>
                                        </Col>
                                    </Container>
                                </Col>
                            </Container>
                        ) : null}
                    </Form>
                </MainContainer>
            </>
        );
    }
}

const mapStateToProps = (state) => {
    const lastHref = state.status?.lastHref;
    const lastHrefPathname = lastHref && typeof lastHref.pathname === "string" ? lastHref.pathname : "";
    return {
        credentials: compose(
            (array) => array.filter((item) => item !== "accessToken"),
            removeDuplicateItems,
            flattenArray,
            (array) => array.map(({ credentials }) => credentials),
        )(formSelectors.getCredentialsGroups(state)),
        fetching: formSelectors.getFetching(state),
        signTransactionData: formSelectors.getSignTransactionData(state),
        user: sessionSelectors.getUser(state),
        potentialSignaturesData: transactionsSelectors.getPotentialSignaturesData(state),
        summary: transferSelectors.getSummary(state),
        summaryEcomex: ecomexSelector.getSummary(state),
        isSuccessSign: formSelectors.getIsSuccessSign(state),
        isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
        activeEnvironment: sessionSelectors.getActiveEnvironment(state),
        holidays: holidaysSelector.getHolidays(state),
        serverDate: transactionsSelectors.getServerDate(state),
        lastHrefPathname,
    };
};

export default compose(
    connect(mapStateToProps),
    withFormik({
        validateOnChange: false,
        validateOnBlur: false,
        mapPropsToValues: ({ credentials }) =>
            credentials.reduce((values, credential) => ({ ...values, [credential]: "" }), { otp: "" }),
        validationSchema: ({ credentials }) =>
            Yup.object().shape(
                credentials.reduce(
                    (values, credential) => ({
                        ...values,
                        [credential]: Yup.string().required(i18n.get(`form.credential.${credential}.required`)),
                    }),
                    {},
                ),
            ),
        handleSubmit: (credentials, formikBag) => {
            const { dispatch, signTransactionData, user, summaryEcomex } = formikBag.props;

            if (signTransactionData?.idActivity !== "ecomex.send.accept.transaction") {
                dispatch(
                    formActions.signTransaction({
                        credentials,
                        formikBag,
                        idTransaction: signTransactionData?.idTransaction,
                        idForm: signTransactionData?.idForm,
                        idActivity: signTransactionData?.idActivity,
                        userId: user.userId,
                        pendingDispatch: signTransactionData?.pendingDispatch,
                        useTransactionData: signTransactionData?.useTransactionData,
                    }),
                );
            } else {
                const ecomexTransaction = {
                    userId: user.userId,
                    idEnvironment: summaryEcomex?.idEnvironment,
                    lang: summaryEcomex?.lang,
                    channel: summaryEcomex?.channel,
                    transactionData: summaryEcomex.transactionData,
                    idTransaction: summaryEcomex?.idTransaction,
                    ecomexRequest: summaryEcomex?.transactionData.requestIdEcomex,
                    deleting: false,
                };
                dispatch(
                    formActions.signTransaction({
                        credentials,
                        formikBag,
                        idTransaction: signTransactionData?.idTransaction,
                        idForm: signTransactionData?.idForm,
                        idActivity: signTransactionData?.idActivity,
                        userId: user.userId,
                        pendingDispatch: signTransactionData?.pendingDispatch,
                        useTransactionData: ecomexTransaction.transactionData,
                    }),
                );
            }
        },
    }),
    resizableRoute,
)(TransactionTicketSignConfirmation);
